import { Session } from 'next-auth';
import { redirect } from 'next/navigation';
import Script from 'next/script';

import { cmsService } from '@services/cms';
import { getAccordionSeo } from '@services/cms/accordion-seo';
import { getSlider } from '@services/cms/sliders';
import { getWallConfig } from '@services/config';
import { personServices } from '@services/person';
import { getWallCurrentPlan } from '@services/products';
import { stickersService } from '@services/products/stickers';
import { wallService } from '@services/products/wall';

import { WallType } from '@app-types/config';
import { ContractType } from '@app-types/contract';
import { SearchParamsType } from '@app-types/page';
import { UserType } from '@app-types/user';

import { ImageThumbor } from '@components/image-thumbor';

import { PAGE_SLUG } from '@helpers/partials';
import { isPro } from '@helpers/person';
import { generateHref, getActiveFilters } from '@helpers/seo';

import { PlanSubTypeId } from '@constants/plan';

import { getStickersPostions } from '@product-wall/helpers';

import { Wall } from './wall';

const wallConfig = getWallConfig(WallType.PHONE);

interface PhoneWallProps {
    isRenewal?: boolean;
    contract?: ContractType;
    contractId?: string;
    searchParams?: SearchParamsType;
    params?: { filter: string };
    session?: Session | null;
}

export async function PhoneWall({
    isRenewal = false,
    contract,
    contractId,
    searchParams,
    params,
    session,
}: Readonly<PhoneWallProps>) {
    const urlSearchParams = new URLSearchParams(searchParams);
    const page = typeof searchParams?.page === 'string' ? Number(searchParams.page) : 1;
    const sort = typeof searchParams?.sort === 'string' ? searchParams.sort : 'meilleures-ventes';
    const activeFilters = getActiveFilters(wallConfig, params?.filter ?? '', urlSearchParams);
    const search = searchParams?.nom;
    let filters = Object.values(wallConfig.filters);
    const user = session?.user as UserType | undefined;
    const planPromise = getWallCurrentPlan(
        WallType.PHONE,
        urlSearchParams,
        undefined,
        isRenewal,
        contract,
        user,
        activeFilters.hasOwnProperty('sousType'),
    );
    const stickersPromise = stickersService.get(activeFilters, isRenewal, search ?? null);

    const [planWithProvenance, stickers, idPu, palpatineModelFiltersResponse] = await Promise.all([
        planPromise,
        stickersPromise,
        user ? personServices.getIdPuFromContract(user, contract?.id) : Promise.resolve(undefined),
        cmsService.getModelFilter(),
    ]);

    const stickerPositions: string[] = getStickersPostions(stickers);
    const palpatineModelFilters = palpatineModelFiltersResponse?.items?.[0];

    const productsPromise =
        contractId || !isRenewal
            ? wallService.getProduct(
                  WallType.PHONE,
                  {
                      sort,
                      page,
                      limit: wallConfig.productByPage,
                      filters: activeFilters,
                      plan: planWithProvenance.plan?.gencode
                          ? planWithProvenance.plan.gencode
                          : (planWithProvenance.plan as undefined | null),
                      search,
                      contract: contractId,
                      stickers: stickerPositions,
                  },
                  session === null,
              )
            : Promise.resolve({ products: [], count: 0 });
    const sliderPromise = getSlider(wallConfig, activeFilters, isRenewal);
    const accordionSeoPromise = getAccordionSeo(wallConfig, activeFilters);

    const [{ products, count }, slider, accordionSeo, person] = await Promise.all([
        productsPromise,
        sliderPromise,
        accordionSeoPromise,
        user && idPu ? personServices.getPersonDetails(idPu, user) : Promise.resolve(undefined),
    ]);

    const initialUrl = generateHref(wallConfig, activeFilters, urlSearchParams, false);

    if (isPro(person)) {
        delete activeFilters['sousType'];
        filters = filters.filter((f) => !f.isHiddenForPro);
    } else {
        if (planWithProvenance?.plan?.subTypeId === PlanSubTypeId.SMART_CHANGE) {
            activeFilters['sousType'] = ['smart-change'];
        } else {
            delete activeFilters['sousType'];
        }
    }

    const generatedUrl = generateHref(wallConfig, activeFilters, urlSearchParams, false);

    if (generatedUrl !== initialUrl) {
        redirect(`${wallConfig.baseUrl}${isRenewal ? '/renouvellement' : ''}${generatedUrl}`);
    }

    return (
        <>
            <Wall
                title={wallConfig.baseTitle}
                type={WallType.PHONE}
                products={products}
                pageSize={wallConfig.productByPage}
                count={count}
                wallConfig={wallConfig}
                filters={filters}
                startPage={page}
                activeFilters={activeFilters}
                activeSort={sort}
                searchParams={searchParams}
                slider={slider}
                accordionSeo={accordionSeo}
                stickers={stickers}
                plan={planWithProvenance}
                enableKonami
                enableHandsFree
                enableCompare
                search={search}
                isRenewal={isRenewal}
                user={user}
                contract={contract}
                isLoading={isRenewal && !contractId}
                imageAs={ImageThumbor}
                slug={PAGE_SLUG.PHONE_WALL}
                palpatineModelFilters={palpatineModelFilters}
                trackingData={{
                    page: 'Mur_des_telephones',
                    pageCategory: 'Liste_produits_telephone',
                    marketLine: 'Mobile',
                    isB2B: false,
                    eCommerceStep: 'Liste_produits',
                    variant: { Recommandation: products.some((product) => product.recommended) },
                }}
            />
            <Script src='/statics/banners/scripts/banners.js' strategy='afterInteractive' />
        </>
    );
}
